/**
 * CSV field serialization for client-side exports.
 *
 * Two separate concerns:
 *
 *   1. RFC4180 quoting, so the file parses back into the fields we wrote.
 *   2. Formula-sigil neutralization, so a spreadsheet application does not
 *      execute a field it reads as a formula.
 *
 * Excel, LibreOffice and Google Sheets treat a cell whose first character is
 * `=`, `+`, `-`, `@`, TAB or CR as a formula (CWE-1236). A bookmark note, a
 * tag, or a post title that opens with `=HYPERLINK(...)` would otherwise land
 * in the export as a live formula.
 *
 * Neutralization prefixes a single quote, which spreadsheets read as "this
 * cell is text". It is applied before quoting so the escaped form stays valid
 * RFC4180. It changes the displayed value, which is the accepted cost of not
 * executing it. A plain numeric literal such as `-5` or `+1.5e3` is left
 * alone: it evaluates to itself, and prefixing it would turn a number column
 * into text.
 */

/** Leading characters a spreadsheet may read as the start of a formula. */
const FORMULA_SIGILS = new Set(['=', '+', '-', '@', '\t', '\r']);

/** A bare signed decimal number, which a spreadsheet reads as a value. */
const NUMERIC_LITERAL = /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;

/** Prefix `'` to a value whose first character is a formula sigil. */
export function neutralizeFormula(value: string): string {
  if (value.length === 0 || !FORMULA_SIGILS.has(value[0])) return value;
  return NUMERIC_LITERAL.test(value) ? value : `'${value}`;
}

/**
 * Serialize one CSV field: neutralize a formula sigil, then quote per RFC4180.
 * Quotes only when needed unless `alwaysQuote` is set. `null`/`undefined`
 * render as an empty field.
 */
export function csvField(
  value: string | null | undefined,
  { alwaysQuote = false }: { alwaysQuote?: boolean } = {}
): string {
  const s = neutralizeFormula(value ?? '');
  return alwaysQuote || /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
